import { FormControl, HFlow, Icon, isEmpty, Text, Theme, useTheme, VFlow } from 'bold-ui'
import useSession from 'components/auth/useSession'
import { ErrorField } from 'components/form'
import { LoadingIndicator } from 'components/loading'
import { useFlags } from 'config/useFlagsContext'
import { css } from 'emotion'
import { useProblemasFaltantesLpcQuery } from 'graphql/hooks.generated'
import { AcessoCbo } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import React, { useMemo } from 'react'
import { MetaPath } from 'util/metaPath'

import { AvaliacaoFormModel } from '../../AvaliacaoForm'
import { SugestaoProblemasIncluirLPCForm } from './SugestaoProblemasIncluirLPCForm'

interface SugestaoProblemaHistoricoClinicoProps {
  name: MetaPath<AvaliacaoFormModel>
}

export function SugestaoProblemaHistoricoClinico(props: Readonly<SugestaoProblemaHistoricoClinicoProps>) {
  const atendimento = useAtendimentoContext()
  const theme = useTheme()
  const styles = createStyles(theme)
  const { PILOTO_DIABETES_HIPERTENSAO_LPC } = useFlags()

  const { hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })
  const isMedicoOuEnfermeiro = useMemo(() => hasCboAuth([AcessoCbo.MEDICO, AcessoCbo.ENFERMEIRO]), [hasCboAuth])

  const { data, loading } = useProblemasFaltantesLpcQuery({
    variables: { prontuarioId: atendimento.prontuario.id },
    skip: !PILOTO_DIABETES_HIPERTENSAO_LPC,
  })
  if (!loading && isEmpty(data?.problemasFaltantesLPC)) {
    return null
  }

  return (
    <VFlow style={styles.outsideBorder}>
      <HFlow style={styles.header} alignItems='center' hSpacing={0.5}>
        <Icon icon='exclamationTriangleOutline' color={theme.pallete.status.alert.main} />
        <FormControl
          required={isMedicoOuEnfermeiro}
          style={styles.title}
          label='Problemas/Condições identificados no histórico clínico do cidadão'
        />
      </HFlow>
      {loading ? (
        <VFlow style={styles.textPadding}>
          <LoadingIndicator />
        </VFlow>
      ) : (
        <VFlow style={styles.textPadding} vSpacing={0.5}>
          <ErrorField name={props.name.hasSubmittedSugestoesDeProblemas} />
          <Text>
            Para aprimorar a qualidade e a completude dos registros no prontuário eletrônico, identificamos{' '}
            {data.problemasFaltantesLPC?.length} problemas ou condições no histórico clínico, registrados nos últimos 36
            meses. Por favor, revise e classifique cada um deles como <Text fontWeight='bold'>ativo </Text>
            ou <Text fontWeight='bold'>resolvido </Text>
            na lista de problemas e condições do prontuário, contribuindo para um acompanhamento mais preciso e seguro.
          </Text>
          <Text variant='h5'>Incluir na lista de problemas/condições com a situação definida como:</Text>
          <SugestaoProblemasIncluirLPCForm
            ciapsCids={data.problemasFaltantesLPC}
            name={props.name}
            isMedicoOuEnfermeiro={isMedicoOuEnfermeiro}
          ></SugestaoProblemasIncluirLPCForm>
        </VFlow>
      )}
    </VFlow>
  )
}

const createStyles = (theme: Theme) => {
  return {
    outsideBorder: css`
      border: 1px ${theme.pallete.divider} solid;
    `,
    header: css`
      border-bottom: 1px ${theme.pallete.divider} solid;
      padding: 0.5rem 1rem 0.5rem 1rem;
    `,
    textPadding: css`
      padding: 0rem 1rem 1rem 1rem;
    `,
    title: css`
      > label {
        font-size: 16px;
        margin-bottom: 0px;
      }
    `,
  }
}
